import { isRMCertified } from "../../../shared/rmCertification";
import { Badge, Button, Checkbox, Grid, Group, Loader, NumberFormatter, Select, SimpleGrid, Stack, Text, TextInput, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconEye, IconMoneybag, IconReceipt, IconReceiptRefund, IconSearch, IconWriting } from "@tabler/icons-react";
import { collectionGroup, doc, DocumentData, endBefore, getCountFromServer, getDoc, getDocs, limit, limitToLast, orderBy, query, QueryConstraint, QueryDocumentSnapshot, startAfter, Timestamp, where } from "firebase/firestore";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";

import FilterModal, { CustomFilterSection, FilterItem, FilterSection } from "./FilterModal";
import { FilterBadgeGroup } from "./FilterBadges";
import { CurrentViewers } from "./current_viewers";
import { TagBadge } from "./tag_badge";
import { GlobalDataContext, MyUserDataContext } from "../contexts";
import { dateFormater } from "../helpers/common";
import { useFirestore } from "../helpers/firebaseContext";
import { KYCStatusCode, Order, OrderStatusCode, RMUser, TypesenseOrder } from "../types";
import type { OrdersSummaryFilterRequest } from "./orders_instant_search";

type OrderDateFilterField = "created" | "approvedAt" | "expected_nav_date";

interface OrderDateFilterState {
    field: OrderDateFilterField;
    from: string | null;
    to: string | null;
}

interface OrdersFirestoreSearchProps {
    setSelectedRows?: (rows: TypesenseOrder[]) => void;
    ActionsComponent?: ({ orderObj }: { orderObj: Order }) => JSX.Element;
    allowShowAll?: boolean;
    summaryFilterRequest?: OrdersSummaryFilterRequest | null;
    refreshToken?: number;
    topElements?: JSX.Element;
    additionalChildren?: JSX.Element;
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_DATE_FILTER_STATE: OrderDateFilterState = {
    field: "created",
    from: null,
    to: null,
};
const ORDER_DATE_FILTER_OPTIONS: Array<{ value: OrderDateFilterField; label: string }> = [
    { value: "created", label: "Created date" },
    { value: "approvedAt", label: "Approval date" },
    { value: "expected_nav_date", label: "Expected NAV date" },
];
const ORDER_DATE_FILTER_LABELS: Record<OrderDateFilterField, string> = {
    created: "Created date",
    approvedAt: "Approval date",
    expected_nav_date: "Expected NAV date",
};
const ORDER_TYPE_OPTIONS = [
    { value: "subscription", label: "subscription" },
    { value: "redemption", label: "redemption" },
];
const FIRESTORE_IN_QUERY_LIMIT = 10;

type FirestoreWhereOperator = "==" | "in";

interface VisibilityConstraintDefinition {
    field: string;
    operator: FirestoreWhereOperator;
    value: string | string[];
}

type VisibilityConstraintBranch = VisibilityConstraintDefinition[];

interface KfhVisibilityProfile {
    email: string;
    rmId: string;
    rmSegment: string;
    rmAvailableSegments: string[];
    certified?: boolean;
}

const toStartOfDay = (date: Date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
};

const buildLocalDateFromString = (value: string): Date | null => {
    const [year, month, day] = value.split("-").map(Number);

    if (!year || !month || !day) {
        return null;
    }

    const parsedDate = new Date(year, month - 1, day);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const toDateInputString = (value: Date | string | null) => {
    if (!value) {
        return null;
    }

    if (typeof value === "string") {
        return value;
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, "0");
    const day = `${value.getDate()}`.padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const toSearchDate = (value: unknown): Date | null => {
    if (!value) {
        return null;
    }

    if (value instanceof Timestamp) {
        return value.toDate();
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "number") {
        const parsedDate = new Date(value > 1_000_000_000_000 ? value : value * 1000);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === "string") {
        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    if (typeof value === "object" && value !== null) {
        if ("toDate" in value && typeof value.toDate === "function") {
            const parsedDate = value.toDate();
            return parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime()) ? parsedDate : null;
        }

        if ("seconds" in value && typeof value.seconds === "number") {
            const parsedDate = new Date(value.seconds * 1000);
            return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
        }
    }

    return null;
};

const getExpectedNavBadgeState = (expectedNavDate: Date) => {
    const today = toStartOfDay(new Date());
    const targetDate = toStartOfDay(expectedNavDate);
    const dayDiff = Math.round((targetDate.getTime() - today.getTime()) / DAY_IN_MS);

    if (dayDiff < 0) {
        return {
            color: "red",
            tooltip: `Overdue by ${Math.abs(dayDiff)} day(s)`,
        };
    }

    if (dayDiff === 0) {
        return {
            color: "orange",
            tooltip: "Expected today",
        };
    }

    if (dayDiff === 1) {
        return {
            color: "yellow",
            tooltip: "Expected tomorrow",
        };
    }

    return {
        color: "green",
        tooltip: `Expected in ${dayDiff} day(s)`,
    };
};

const formatDateFilterSummary = (value: OrderDateFilterState) => {
    const fromDate = value.from ? buildLocalDateFromString(value.from) : null;
    const toDate = value.to ? buildLocalDateFromString(value.to) : null;

    if (!fromDate && !toDate) {
        return null;
    }

    const dateLabel = ORDER_DATE_FILTER_LABELS[value.field];

    if (fromDate && toDate) {
        return `${dateLabel}: ${dateFormater(fromDate)} to ${dateFormater(toDate)}`;
    }

    if (fromDate) {
        return `${dateLabel}: from ${dateFormater(fromDate)}`;
    }

    return `${dateLabel}: until ${dateFormater(toDate!)}`;
};

const normalizeSegments = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value
            .map((segment) => `${segment ?? ""}`.trim())
            .filter((segment) => segment.length > 0);
    }

    if (typeof value === "string") {
        return value
            .split(",")
            .map((segment) => segment.trim())
            .filter((segment) => segment.length > 0);
    }

    return [];
};

const chunkValues = <T,>(values: T[], size: number) => {
    const chunks: T[][] = [];

    for (let index = 0; index < values.length; index += size) {
        chunks.push(values.slice(index, index + size));
    }

    return chunks;
};

const dedupeVisibilityBranches = (branches: VisibilityConstraintBranch[]) => {
    const seen = new Set<string>();

    return branches.filter((branch) => {
        const key = JSON.stringify(branch);

        if (seen.has(key)) {
            return false;
        }

        seen.add(key);
        return true;
    });
};

const getDateFieldValue = (order: Order, field: OrderDateFilterField) => {
    if (field === "created") {
        return toSearchDate(order.created);
    }

    if (field === "approvedAt") {
        return toSearchDate((order as any).approvedAt);
    }

    return toSearchDate(order.expected_nav_date);
};

const matchesDateFilter = (order: Order, filterState: OrderDateFilterState) => {
    if (!filterState.from && !filterState.to) {
        return true;
    }

    const orderDate = getDateFieldValue(order, filterState.field);
    if (!orderDate) {
        return false;
    }

    const fromDate = filterState.from ? buildLocalDateFromString(filterState.from) : null;
    const toDate = filterState.to ? buildLocalDateFromString(filterState.to) : null;

    if (fromDate && orderDate < fromDate) {
        return false;
    }

    if (toDate) {
        const endOfDay = new Date(toDate);
        endOfDay.setDate(endOfDay.getDate() + 1);
        if (orderDate >= endOfDay) {
            return false;
        }
    }

    return true;
};

const matchesSummaryFilter = (
    order: Order,
    summaryFilterRequest: OrdersSummaryFilterRequest | null,
    summaryFundId: string | null,
) => {
    if (!summaryFilterRequest) {
        return true;
    }

    if (summaryFundId) {
        const fundId = order.fund?.id || (order.fund as any)?.code || "";
        if (fundId !== summaryFundId) {
            return false;
        }
    }

    if (order.tags?.orderStatus?.code !== summaryFilterRequest.orderStatusCode) {
        return false;
    }

    const expectedNavDate = getDateFieldValue(order, "expected_nav_date");
    const summaryDate = buildLocalDateFromString(summaryFilterRequest.expectedNavDate);

    if (!expectedNavDate || !summaryDate) {
        return false;
    }

    const nextDay = new Date(summaryDate);
    nextDay.setDate(nextDay.getDate() + 1);

    return expectedNavDate >= summaryDate && expectedNavDate < nextDay;
};

const sortOrdersByFieldDesc = (orders: Order[], field: OrderDateFilterField) => {
    return [...orders].sort((left, right) => {
        const leftDate = getDateFieldValue(left, field) ?? getDateFieldValue(left, "created");
        const rightDate = getDateFieldValue(right, field) ?? getDateFieldValue(right, "created");

        return (rightDate?.getTime() ?? 0) - (leftDate?.getTime() ?? 0);
    });
};

const buildKfhVisibilityBranches = (
    customClaims: Record<string, any>,
    profile: KfhVisibilityProfile,
) => {
    const branches: VisibilityConstraintBranch[] = [];

    const addEqualityBranch = (field: string, value: string) => {
        if (value) {
            branches.push([{ field, operator: "==", value }]);
        }
    };

    const { email, rmId, rmSegment, rmAvailableSegments } = profile;

    if (customClaims.kfh_oper) {
        if (rmAvailableSegments.length > 0) {
            chunkValues(rmAvailableSegments, FIRESTORE_IN_QUERY_LIMIT).forEach((segments) => {
                branches.push([{ field: "rm.segment", operator: "in", value: segments }]);
            });
        } else if (rmSegment) {
            branches.push([{ field: "rm.segment", operator: "==", value: rmSegment }]);
        } else {
            addEqualityBranch("rm.email", email);
            addEqualityBranch("rm.order_handler_email", email);
            addEqualityBranch("referredBy.email", email);
            addEqualityBranch("submittedBy.email", email);
            addEqualityBranch("rm.rm_id", rmId);
        }

        return dedupeVisibilityBranches(branches);
    }

    addEqualityBranch("rm.email", email);
    addEqualityBranch("rm.order_handler_email", email);
    addEqualityBranch("referredBy.email", email);
    addEqualityBranch("submittedBy.email", email);
    addEqualityBranch("rm.rm_id", rmId);

    if (isRMCertified(customClaims, profile) && rmSegment) {
        branches.push([
            { field: "tags.orderStatus.code", operator: "==", value: OrderStatusCode.waiting_certified_rm },
            { field: "rm.segment", operator: "==", value: rmSegment },
        ]);
    }

    if (customClaims.kfh_rm_approver && rmSegment) {
        branches.push([
            { field: "tags.orderStatus.code", operator: "==", value: OrderStatusCode.selling_agent_review },
            { field: "rm.segment", operator: "==", value: rmSegment },
        ]);
        branches.push([
            { field: "tags.orderStatus.code", operator: "==", value: KYCStatusCode.selling_agent_update },
            { field: "rm.segment", operator: "==", value: rmSegment },
        ]);
    }

    return dedupeVisibilityBranches(branches);
};

const normalizeSearchValue = (value: unknown) => {
    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "string") {
        return value.toLowerCase();
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value).toLowerCase();
    }

    return "";
};

const getSearchableText = (order: Order) => {
    return [
        order.id,
        order.type,
        order.customerObj?.name_en,
        order.customerObj?.name_ar,
        order.customerObj?.mobile,
        order.customerObj?.civilID,
        order.customerObj?.commercial_register,
        order.fund?.name_en,
        order.fund?.name_ar,
        order.tags?.orderStatus?.code,
        order.tags?.orderStatus?.title,
        order.tags?.kycStatus?.code,
        order.tags?.kycStatus?.title,
        order.rm?.name_en,
        order.rm?.name_ar,
        order.rm?.rm_id,
        order.referredBy?.email,
        order.submittedBy?.email,
    ]
        .map((value) => normalizeSearchValue(value))
        .filter(Boolean)
        .join(" ");
};

const getRestrictedStatuses = (customClaims: Record<string, any>) => {
    const orderStatuses: string[] = [];

    if (customClaims.rm === true || customClaims.kfh_rm === true || customClaims.kfh_oper === true) {
        orderStatuses.push(...[OrderStatusCode.need_slip, OrderStatusCode.need_signature, OrderStatusCode.signature_verification, OrderStatusCode.need_form]);
    }

    if (customClaims.cr === true) {
        orderStatuses.push(...[OrderStatusCode.review, OrderStatusCode.approval, OrderStatusCode.waiting_for_kyc, OrderStatusCode.signature_verification]);
    }

    if (customClaims.oper === true) {
        orderStatuses.push(...[OrderStatusCode.processing, OrderStatusCode.waiting_for_nav]);
    }

    return orderStatuses;
};

const buildStaticFilterItems = (
    options: Array<{ value: string; label: string }>,
    selectedValues: string[]
): FilterItem[] => {
    return options.map((option) => ({
        value: option.value,
        label: option.label,
        count: 0,
        isRefined: selectedValues.includes(option.value),
    }));
};

const OrdersFirestoreSearch = ({
    setSelectedRows,
    ActionsComponent,
    allowShowAll = true,
    summaryFilterRequest = null,
    refreshToken = 0,
    topElements,
    additionalChildren,
}: OrdersFirestoreSearchProps) => {
    const db = useFirestore();
    const userData = useContext(MyUserDataContext);
    const globalDataContext = useContext(GlobalDataContext);
    const customClaims = (userData.firebaseTokenResult?.claims.customClaims as Record<string, any>) || {};
    const kfhUserEmail = `${userData.user?.email ?? ""}`.trim();
    const requiresKfhVisibilityScope = Boolean(customClaims.kfh_rm || customClaims.kfh_oper);

    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchText, setSearchText] = useState("");
    const [openFilterModal, setOpenFilterModal] = useState(false);
    const [showAllOrders, setShowAllOrders] = useState(true);
    const [fundFilters, setFundFilters] = useState<string[]>([]);
    const [orderTypeFilters, setOrderTypeFilters] = useState<string[]>([]);
    const [orderStatusFilters, setOrderStatusFilters] = useState<string[]>([]);
    const [kycStatusFilters, setKycStatusFilters] = useState<string[]>([]);
    const [dateFilterState, setDateFilterState] = useState<OrderDateFilterState>(DEFAULT_DATE_FILTER_STATE);
    const [currentPage, setCurrentPage] = useState(1);
    const [previousPageNumber, setPreviousPageNumber] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [totalRows, setTotalRows] = useState(0);
    const [clearSelectedRowsToggle, setClearSelectedRowsToggle] = useState(false);
    const [kfhVisibilityProfile, setKfhVisibilityProfile] = useState<KfhVisibilityProfile | null | undefined>(
        requiresKfhVisibilityScope ? undefined : null
    );

    const firstDocInCurrentPageRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
    const lastDocInCurrentPageRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
    const previousServerQueryKeyRef = useRef<string | null>(null);

    const restrictedStatuses = useMemo(() => getRestrictedStatuses(customClaims), [customClaims]);
    const summaryFund = useMemo(
        () => globalDataContext?.funds?.find((fund) =>
            fund.name_en === summaryFilterRequest?.fundName ||
            fund.name_ar === summaryFilterRequest?.fundName ||
            fund.short_name === summaryFilterRequest?.fundName
        ),
        [globalDataContext?.funds, summaryFilterRequest?.fundName]
    );
    const summaryFundId = summaryFund?.id || (summaryFund as any)?.code || null;
    const visibilityBranches = useMemo(() => {
        if (!requiresKfhVisibilityScope) {
            return null;
        }

        if (kfhVisibilityProfile === undefined) {
            return undefined;
        }

        if (!kfhVisibilityProfile) {
            return [];
        }

        return buildKfhVisibilityBranches(customClaims, kfhVisibilityProfile);
    }, [customClaims, kfhVisibilityProfile, requiresKfhVisibilityScope]);
    const usingKfhVisibilityFallback = Array.isArray(visibilityBranches);
    const visibilityBranchKey = useMemo(
        () => visibilityBranches === undefined ? "loading" : JSON.stringify(visibilityBranches),
        [visibilityBranches]
    );

    useEffect(() => {
        if (!requiresKfhVisibilityScope) {
            setKfhVisibilityProfile(null);
            return;
        }

        const fallbackProfile: KfhVisibilityProfile = {
            email: kfhUserEmail,
            rmId: "",
            rmSegment: `${customClaims.segment ?? ""}`.trim(),
            rmAvailableSegments: normalizeSegments(customClaims.availableSegments),
        };

        if (!kfhUserEmail) {
            setKfhVisibilityProfile(fallbackProfile);
            return;
        }

        let active = true;
        setKfhVisibilityProfile(undefined);

        getDoc(doc(db, "rms", kfhUserEmail))
            .then((rmSnapshot) => {
                if (!active) {
                    return;
                }

                if (!rmSnapshot.exists()) {
                    setKfhVisibilityProfile(fallbackProfile);
                    return;
                }

                const rmData = rmSnapshot.data() as Partial<RMUser>;
                setKfhVisibilityProfile({
                    email: `${rmData.email || fallbackProfile.email}`.trim(),
                    rmId: `${rmData.rm_id ?? ""}`.trim(),
                    certified: rmData.certified === true,
                    rmSegment: `${rmData.segment ?? fallbackProfile.rmSegment}`.trim(),
                    rmAvailableSegments: normalizeSegments(rmData.availableSegments ?? fallbackProfile.rmAvailableSegments),
                });
            })
            .catch(() => {
                if (active) {
                    setKfhVisibilityProfile(fallbackProfile);
                }
            });

        return () => {
            active = false;
        };
    }, [
        customClaims.availableSegments,
        customClaims.segment,
        db,
        kfhUserEmail,
        requiresKfhVisibilityScope,
    ]);

    useEffect(() => {
        setClearSelectedRowsToggle((current) => !current);
        setSelectedRows?.([]);
    }, [currentPage, rowsPerPage, refreshToken, summaryFilterRequest?.requestId, showAllOrders, setSelectedRows]);

    useEffect(() => {
        if (!summaryFilterRequest) {
            return;
        }

        setDateFilterState({
            field: "expected_nav_date",
            from: summaryFilterRequest.expectedNavDate,
            to: summaryFilterRequest.expectedNavDate,
        });
        setFundFilters(summaryFundId ? [summaryFundId] : []);
        setOrderStatusFilters(summaryFilterRequest.orderStatusCode ? [summaryFilterRequest.orderStatusCode] : []);
    }, [summaryFilterRequest, summaryFundId]);

    const serverQueryConfig = useMemo(() => {
        const queryConstraints: QueryConstraint[] = [];
        const orderField: OrderDateFilterField = summaryFilterRequest ? "expected_nav_date" : "created";
        const scopedByRole = allowShowAll && !showAllOrders && restrictedStatuses.length > 0;
        const blockedByRoleScope = Boolean(
            scopedByRole &&
            summaryFilterRequest &&
            !restrictedStatuses.includes(summaryFilterRequest.orderStatusCode)
        );

        if (blockedByRoleScope) {
            return {
                blocked: true,
                orderField,
                queryConstraints,
            };
        }

        if (scopedByRole && !summaryFilterRequest) {
            queryConstraints.push(where("tags.orderStatus.code", "in", restrictedStatuses));
        }

        if (summaryFilterRequest) {
            if (summaryFundId) {
                queryConstraints.push(where("fund.id", "==", summaryFundId));
            }

            queryConstraints.push(where("tags.orderStatus.code", "==", summaryFilterRequest.orderStatusCode));

            const summaryDate = buildLocalDateFromString(summaryFilterRequest.expectedNavDate);
            if (summaryDate) {
                const nextDay = new Date(summaryDate);
                nextDay.setDate(nextDay.getDate() + 1);
                queryConstraints.push(where("expected_nav_date", ">=", summaryDate));
                queryConstraints.push(where("expected_nav_date", "<", nextDay));
            }
        }

        return {
            blocked: false,
            orderField,
            queryConstraints,
        };
    }, [allowShowAll, restrictedStatuses, showAllOrders, summaryFilterRequest, summaryFundId]);

    const serverQueryKey = useMemo(() => JSON.stringify({
        allowShowAll,
        refreshToken,
        restrictedStatuses,
        showAllOrders,
        summaryFilterRequest,
        summaryFundId,
        rowsPerPage,
        visibilityBranchKey,
    }), [allowShowAll, refreshToken, restrictedStatuses, rowsPerPage, showAllOrders, summaryFilterRequest, summaryFundId, visibilityBranchKey]);

    useEffect(() => {
        let active = true;

        const loadOrders = async () => {
            if (requiresKfhVisibilityScope && visibilityBranches === undefined) {
                setLoading(true);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                if (Array.isArray(visibilityBranches)) {
                    if (visibilityBranches.length === 0) {
                        firstDocInCurrentPageRef.current = null;
                        lastDocInCurrentPageRef.current = null;
                        previousServerQueryKeyRef.current = null;
                        setOrders([]);
                        setTotalRows(0);
                        setCurrentPage(1);
                        setPreviousPageNumber(1);
                        return;
                    }

                    const scopedSnapshots = await Promise.all(
                        visibilityBranches.map((branch) =>
                            getDocs(
                                query(
                                    collectionGroup(db, "orders"),
                                    ...branch.map((constraint) =>
                                        where(constraint.field, constraint.operator, constraint.value as any)
                                    )
                                )
                            )
                        )
                    );

                    if (!active) {
                        return;
                    }

                    const scopedDocs = new Map<string, QueryDocumentSnapshot<DocumentData>>();
                    scopedSnapshots.forEach((snapshot) => {
                        snapshot.docs.forEach((docSnapshot) => {
                            scopedDocs.set(docSnapshot.id, docSnapshot);
                        });
                    });

                    let scopedOrders = Array.from(scopedDocs.values()).map((docSnapshot) => ({
                        ...(docSnapshot.data() as Order),
                        id: docSnapshot.id,
                    }));

                    if (summaryFilterRequest) {
                        scopedOrders = scopedOrders.filter((order) => matchesSummaryFilter(order, summaryFilterRequest, summaryFundId));
                    } else if (allowShowAll && !showAllOrders && restrictedStatuses.length > 0) {
                        scopedOrders = scopedOrders.filter((order) =>
                            restrictedStatuses.includes(order.tags?.orderStatus?.code || "")
                        );
                    }

                    const orderField: OrderDateFilterField = summaryFilterRequest ? "expected_nav_date" : "created";
                    const sortedOrders = sortOrdersByFieldDesc(scopedOrders, orderField);
                    const scopedTotalRows = sortedOrders.length;
                    const scopedTotalPages = scopedTotalRows > 0 ? Math.ceil(scopedTotalRows / rowsPerPage) : 1;
                    const safePage = Math.min(currentPage, scopedTotalPages);
                    const startIndex = (safePage - 1) * rowsPerPage;
                    const pagedOrders = sortedOrders.slice(startIndex, startIndex + rowsPerPage);

                    firstDocInCurrentPageRef.current = null;
                    lastDocInCurrentPageRef.current = null;
                    previousServerQueryKeyRef.current = null;
                    setOrders(pagedOrders);
                    setTotalRows(scopedTotalRows);
                    setCurrentPage(safePage);
                    setPreviousPageNumber(safePage);
                    return;
                }

                if (serverQueryConfig.blocked) {
                    firstDocInCurrentPageRef.current = null;
                    lastDocInCurrentPageRef.current = null;
                    previousServerQueryKeyRef.current = serverQueryKey;
                    setOrders([]);
                    setTotalRows(0);
                    setCurrentPage(1);
                    setPreviousPageNumber(1);
                    return;
                }

                const baseQuery = query(
                    collectionGroup(db, "orders"),
                    ...serverQueryConfig.queryConstraints,
                    orderBy(serverQueryConfig.orderField, "asc")
                );
                const queryChanged = previousServerQueryKeyRef.current !== serverQueryKey;
                const targetPage = queryChanged ? 1 : currentPage;

                let pagedQuery;

                if (targetPage <= 1 || queryChanged) {
                    pagedQuery = query(baseQuery, limitToLast(rowsPerPage));
                } else if (targetPage > previousPageNumber && firstDocInCurrentPageRef.current) {
                    pagedQuery = query(
                        baseQuery,
                        endBefore(firstDocInCurrentPageRef.current),
                        limitToLast(rowsPerPage)
                    );
                } else if (targetPage < previousPageNumber && lastDocInCurrentPageRef.current) {
                    pagedQuery = query(
                        baseQuery,
                        startAfter(lastDocInCurrentPageRef.current),
                        limit(rowsPerPage)
                    );
                } else {
                    pagedQuery = query(baseQuery, limitToLast(rowsPerPage));
                }

                const [countSnapshot, ordersSnapshot] = await Promise.all([
                    getCountFromServer(baseQuery),
                    getDocs(pagedQuery),
                ]);

                if (!active) {
                    return;
                }

                const docs = ordersSnapshot.docs;
                const fetchedOrders = docs.map((docSnapshot) => ({
                    ...(docSnapshot.data() as Order),
                    id: docSnapshot.id,
                })).reverse();

                firstDocInCurrentPageRef.current = docs[0] ?? null;
                lastDocInCurrentPageRef.current = docs[docs.length - 1] ?? null;
                previousServerQueryKeyRef.current = serverQueryKey;

                setOrders(fetchedOrders);
                setTotalRows(countSnapshot.data().count);
                setCurrentPage(targetPage);
                setPreviousPageNumber(targetPage);
            } catch (loadError: any) {
                if (!active) {
                    return;
                }

                setOrders([]);
                setTotalRows(0);
                setError(loadError?.message || "Failed to load orders from Firestore.");
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadOrders();

        return () => {
            active = false;
        };
    }, [allowShowAll, currentPage, db, previousPageNumber, refreshToken, restrictedStatuses, rowsPerPage, serverQueryConfig, serverQueryKey, showAllOrders, summaryFilterRequest, summaryFundId, visibilityBranches, requiresKfhVisibilityScope]);

    const normalizedSearchText = searchText.trim().toLowerCase();

    const visibleOrders = useMemo(() => {
        return orders.filter((order) => {
            if (normalizedSearchText && !getSearchableText(order).includes(normalizedSearchText)) {
                return false;
            }

            if (fundFilters.length > 0) {
                const fundId = order.fund?.id || (order.fund as any)?.code || "";
                if (!fundFilters.includes(fundId)) {
                    return false;
                }
            }

            if (orderTypeFilters.length > 0 && !orderTypeFilters.includes(order.type || "")) {
                return false;
            }

            if (orderStatusFilters.length > 0 && !orderStatusFilters.includes(order.tags?.orderStatus?.code || "")) {
                return false;
            }

            if (kycStatusFilters.length > 0 && !kycStatusFilters.includes(order.tags?.kycStatus?.code || "")) {
                return false;
            }

            return matchesDateFilter(order, dateFilterState);
        });
    }, [dateFilterState, fundFilters, kycStatusFilters, normalizedSearchText, orderStatusFilters, orderTypeFilters, orders]);

    const fundsItems = useMemo(() => buildStaticFilterItems(
        (globalDataContext?.funds ?? [])
            .map((fund) => ({
                value: fund.id || (fund as any).code,
                label: fund.name_en || fund.short_name || fund.name_ar || fund.id || (fund as any).code || "Unknown fund",
            }))
            .filter((option) => Boolean(option.value) && Boolean(option.label))
            .sort((left, right) => String(left.label).localeCompare(String(right.label))),
        fundFilters
    ), [fundFilters, globalDataContext?.funds]);

    const orderTypeItems = useMemo(
        () => buildStaticFilterItems(ORDER_TYPE_OPTIONS, orderTypeFilters),
        [orderTypeFilters]
    );

    const orderStatusItems = useMemo(
        () => buildStaticFilterItems(
            Object.values(OrderStatusCode).map((value) => ({ value, label: value })),
            orderStatusFilters
        ),
        [orderStatusFilters]
    );

    const kycStatusItems = useMemo(
        () => buildStaticFilterItems(
            Object.values(KYCStatusCode).map((value) => ({ value, label: value })),
            kycStatusFilters
        ),
        [kycStatusFilters]
    );

    const dateFilterSummary = useMemo(
        () => formatDateFilterSummary(dateFilterState),
        [dateFilterState]
    );

    const totalPages = totalRows > 0 ? Math.ceil(totalRows / rowsPerPage) : 1;

    const toggleFilter = (values: string[], setValues: (values: string[]) => void, value: string) => {
        if (values.includes(value)) {
            setValues(values.filter((existingValue) => existingValue !== value));
            return;
        }

        setValues([...values, value]);
    };

    const columns: TableColumn<Order>[] = [
        {
            cell: (row: Order) => {
                const icons = [];

                if (row.type === "subscription") {
                    icons.push(
                        <Tooltip key="subscription" label="Subscription" transitionProps={{ transition: "scale", duration: 500 }}>
                            <ThemeIcon variant="white" color="green">
                                <IconReceipt size={16} />
                            </ThemeIcon>
                        </Tooltip>
                    );
                } else if (row.type === "redemption") {
                    icons.push(
                        <Tooltip key="redemption" label="Redemption" transitionProps={{ transition: "scale", duration: 500 }}>
                            <ThemeIcon variant="white" color="orange">
                                <IconReceiptRefund size={16} />
                            </ThemeIcon>
                        </Tooltip>
                    );
                }

                if (row.tags?.paid) {
                    icons.push(
                        <Tooltip key="paid" label="Paid" transitionProps={{ transition: "scale", duration: 500 }}>
                            <ThemeIcon variant="white" color="yellow">
                                <IconMoneybag size={16} />
                            </ThemeIcon>
                        </Tooltip>
                    );
                }

                if (row.tags?.manual) {
                    icons.push(
                        <Tooltip key="manual" label="Manual" transitionProps={{ transition: "scale", duration: 500 }}>
                            <ThemeIcon variant="white" color="blue">
                                <IconWriting size={16} />
                            </ThemeIcon>
                        </Tooltip>
                    );
                }

                return <>{icons}</>;
            },
            width: "90px",
        },
        {
            name: <Tooltip label="Current Viewers" transitionProps={{ transition: "scale", duration: 500 }}><IconEye size={16} /></Tooltip>,
            cell(row) {
                return <CurrentViewers type="orders" id={row.id!} />;
            },
            width: "90px",
        },
        {
            name: "Name",
            selector: (row: Order) => row.customerObj?.name_en ?? "",
            grow: 1,
        },
        {
            name: "Mobile",
            selector: (row: Order) => row.customerObj?.mobile ?? "",
            grow: 1,
        },
        {
            name: "Civil ID / Commercial register",
            selector: (row: Order) => (row.customerObj?.civilID || row.customerObj?.commercial_register) ?? "",
            grow: 1,
        },
        {
            name: "Fund",
            selector: (row: Order) => row.fund?.name_en ?? "",
        },
        {
            name: "Date",
            cell: (row: Order) => {
                const created = toSearchDate(row.created);

                if (!created) {
                    return row.created ? String(row.created) : "-";
                }

                return created.toLocaleString("en-GB", { hour12: true });
            },
        },
        {
            name: "Expected NAV",
            cell: (row: Order) => {
                const expectedNavDate = toSearchDate(row.expected_nav_date);

                if (!expectedNavDate) {
                    return (
                        <Text size="sm" c="dimmed">
                            -
                        </Text>
                    );
                }

                const badgeState = getExpectedNavBadgeState(expectedNavDate);

                return (
                    <Tooltip label={badgeState.tooltip} transitionProps={{ transition: "scale", duration: 500 }}>
                        <Badge size="sm" variant="light" color={badgeState.color}>
                            {dateFormater(expectedNavDate)}
                        </Badge>
                    </Tooltip>
                );
            },
            grow: 1,
        },
        {
            name: "Units",
            cell: (row: Order) => Number(row.units) === 0 ? (
                <Text size="sm" ta="right">
                    All Units
                </Text>
            ) : (
                <NumberFormatter value={Number(row.units)} thousandSeparator />
            ),
            grow: 1,
        },
        {
            name: "Tags",
            cell: (row: Order) => {
                if (!row.tags) {
                    return null;
                }

                return (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        <TagBadge m={3} tag={row.tags.orderStatus} key="orderStatus" />
                        {row.tags.kycStatus && <TagBadge m={3} tag={row.tags.kycStatus} key="kycStatus" />}
                    </div>
                );
            },
        },
        {
            name: "",
            cell: (row: Order) => ActionsComponent ? <ActionsComponent orderObj={row} /> : null,
            grow: 0,
        },
    ];

    const dateFilterSection = useMemo(() => {
        const fromDate = dateFilterState.from ? buildLocalDateFromString(dateFilterState.from) : null;
        const toDate = dateFilterState.to ? buildLocalDateFromString(dateFilterState.to) : null;

        return {
            id: "date",
            title: "Filter by Date",
            type: "custom",
            selectedCount: dateFilterSummary ? 1 : 0,
            onClear: () => setDateFilterState(DEFAULT_DATE_FILTER_STATE),
            renderContent: () => (
                <Stack gap="sm">
                    <Text size="sm" c="dimmed">
                        Firestore mode pages from the database first, then applies this date filter to the current page.
                    </Text>
                    <SimpleGrid cols={{ base: 1, md: 3 }} spacing="sm" verticalSpacing="sm">
                        <Select
                            label="Date field"
                            data={ORDER_DATE_FILTER_OPTIONS}
                            value={dateFilterState.field}
                            onChange={(value) => {
                                if (!value) {
                                    return;
                                }

                                setDateFilterState((current) => ({
                                    ...current,
                                    field: value as OrderDateFilterField,
                                }));
                            }}
                            allowDeselect={false}
                        />
                        <DatePickerInput
                            label="From"
                            placeholder="Start date"
                            value={fromDate}
                            valueFormat="DD MMM YYYY"
                            clearable
                            maxDate={toDate ?? undefined}
                            onChange={(value) => setDateFilterState((current) => ({
                                ...current,
                                from: toDateInputString(value),
                            }))}
                        />
                        <DatePickerInput
                            label="To"
                            placeholder="End date"
                            value={toDate}
                            valueFormat="DD MMM YYYY"
                            clearable
                            minDate={fromDate ?? undefined}
                            onChange={(value) => setDateFilterState((current) => ({
                                ...current,
                                to: toDateInputString(value),
                            }))}
                        />
                    </SimpleGrid>
                </Stack>
            ),
        } satisfies CustomFilterSection;
    }, [dateFilterState, dateFilterSummary]);

    if (loading) {
        return (
            <Group justify="center" py="xl">
                <Loader />
            </Group>
        );
    }

    if (error) {
        return (
            <Stack gap="sm">
                <Badge color="orange" variant="light">
                    Firestore fallback mode
                </Badge>
                <Text c="red">{error}</Text>
            </Stack>
        );
    }

    return (
        <Grid justify="center" align="center">
            <Grid.Col span={12}>
                <Group justify="space-between" align="flex-start">
                    {topElements}
                    <Group gap="xs" align="center">
                        <Badge color="orange" variant="light">
                            Firestore fallback mode
                        </Badge>
                        <Text size="sm" c="dimmed">
                            {usingKfhVisibilityFallback
                                ? "KFH visibility restrictions mirror the Typesense scoped search key here. Text search and most filters still apply to the current page."
                                : "Orders page is paged from Firestore. Text search and most filters apply to the current page. Use Typesense mode for full-dataset search."}
                        </Text>
                    </Group>
                </Group>

                <Title size="md" my="sm">
                    Search
                </Title>

                <Group align="flex-end">
                    <TextInput
                        leftSection={<IconSearch size={16} />}
                        style={{ flex: 1 }}
                        value={searchText}
                        onChange={(event) => setSearchText(event.currentTarget.value)}
                        placeholder="Filter the current page by customer, fund, status, or ID..."
                    />

                    <Button onClick={() => setOpenFilterModal(true)}>
                        Filter
                    </Button>

                    {allowShowAll && (
                        <Checkbox
                            label="Show all orders"
                            checked={showAllOrders}
                            onChange={(event) => {
                                setShowAllOrders(event.currentTarget.checked);
                                setCurrentPage(1);
                                setPreviousPageNumber(1);
                            }}
                        />
                    )}

                    {additionalChildren}
                </Group>

                <FilterModal
                    opened={openFilterModal}
                    onClose={() => setOpenFilterModal(false)}
                    sections={[
                        dateFilterSection,
                        {
                            id: "funds",
                            title: "Filter by Funds",
                            items: fundsItems,
                            refine: (value) => toggleFilter(fundFilters, setFundFilters, value),
                        },
                        {
                            id: "type",
                            title: "Filter by Order Type",
                            items: orderTypeItems,
                            refine: (value) => toggleFilter(orderTypeFilters, setOrderTypeFilters, value),
                        },
                        {
                            id: "status",
                            title: "Filter by Order Status",
                            items: orderStatusItems,
                            refine: (value) => toggleFilter(orderStatusFilters, setOrderStatusFilters, value),
                        },
                        {
                            id: "kyc_status",
                            title: "Filter by KYC Status",
                            items: kycStatusItems,
                            refine: (value) => toggleFilter(kycStatusFilters, setKycStatusFilters, value),
                        },
                    ] as FilterSection[]}
                />

                <FilterBadgeGroup
                    items={fundsItems}
                    color="grape"
                    refine={(value) => toggleFilter(fundFilters, setFundFilters, value)}
                />
                <FilterBadgeGroup
                    items={orderTypeItems}
                    color="cyan"
                    refine={(value) => toggleFilter(orderTypeFilters, setOrderTypeFilters, value)}
                />
                <FilterBadgeGroup
                    items={orderStatusItems}
                    color="red"
                    refine={(value) => toggleFilter(orderStatusFilters, setOrderStatusFilters, value)}
                />
                <FilterBadgeGroup
                    items={kycStatusItems}
                    color="teal"
                    refine={(value) => toggleFilter(kycStatusFilters, setKycStatusFilters, value)}
                />

                {dateFilterSummary ? (
                    <Group my="sm">
                        <Badge color="indigo">
                            {dateFilterSummary}
                            <Button
                                variant="subtle"
                                c="white"
                                onClick={() => setDateFilterState(DEFAULT_DATE_FILTER_STATE)}
                            >
                                X
                            </Button>
                        </Badge>
                    </Group>
                ) : null}

                <DataTable
                    columns={columns}
                    data={visibleOrders}
                    selectableRows={Boolean(setSelectedRows)}
                    clearSelectedRows={clearSelectedRowsToggle}
                    onSelectedRowsChange={({ selectedRows }) => setSelectedRows?.(
                        selectedRows.map((row) => ({
                            ...(row as Order),
                            customerID: (row as Order).customerObj?.civilID,
                        })) as TypesenseOrder[]
                    )}
                    noDataComponent={(
                        <Text size="sm" c="dimmed" py="lg">
                            No orders on this page match the current page filters.
                        </Text>
                    )}
                />

                <Group justify="space-between" mt="md" align="flex-end">
                    <Stack gap={2}>
                        <Text size="sm">
                            Page {currentPage} of {Math.max(totalPages, 1)}
                        </Text>
                        <Text size="xs" c="dimmed">
                            {usingKfhVisibilityFallback
                                ? `Loaded ${orders.length} rows from the KFH-visible Firestore scope for this page. ${totalRows.toLocaleString()} total rows match the mirrored visibility rules.`
                                : `Loaded ${orders.length} rows from Firestore for this page. ${totalRows.toLocaleString()} total rows match the server-side query.`}
                        </Text>
                        {summaryFilterRequest ? (
                            <Text size="xs" c="dimmed">
                                {usingKfhVisibilityFallback
                                    ? "Summary filters are applied against the mirrored Firestore visibility scope."
                                    : "Summary filters are applied server-side in Firestore mode."}
                            </Text>
                        ) : null}
                    </Stack>

                    <Group align="flex-end">
                        <Select
                            label="Rows per page"
                            value={String(rowsPerPage)}
                            data={[
                                { value: "50", label: "50" },
                                { value: "100", label: "100" },
                                { value: "250", label: "250" },
                            ]}
                            onChange={(value) => {
                                const nextValue = Number(value || DEFAULT_PAGE_SIZE);
                                setRowsPerPage(nextValue);
                                setCurrentPage(1);
                                setPreviousPageNumber(1);
                            }}
                            allowDeselect={false}
                            w={120}
                        />

                        <Button
                            variant="default"
                            disabled={currentPage <= 1}
                            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                        >
                            Newer
                        </Button>
                        <Button
                            variant="default"
                            disabled={currentPage >= totalPages || totalRows === 0}
                            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                        >
                            Older
                        </Button>
                    </Group>
                </Group>
            </Grid.Col>
        </Grid>
    );
};

export default OrdersFirestoreSearch;
